import React, { createContext, useContext, useEffect, useState } from 'react';
import { socket } from '@/utils/socket'; // your existing singleton
import { notificationsAPI } from '@/utils/api';
import { useAuth } from '@/hooks/useAuth';
import { Notification } from '@/types';
import { showSystemNotification } from '@/utils/notificationUtils';

interface NotificationContextType {
  notifications: Notification[];
  setNotifications: React.Dispatch<React.SetStateAction<Notification[]>>;
  unreadCount: number;
  setUnreadCount: React.Dispatch<React.SetStateAction<number>>;
  loading: boolean;
  fetchNotifications: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const NotificationProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentUser } = useAuth(); // get logged user
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  // Fetch notifications from API and update state
  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const res = await notificationsAPI.getMyNotifications();
      if (res.success && Array.isArray(res.data)) {
        setNotifications(res.data);
        setUnreadCount(res.data.filter((n: Notification) => !n.isRead).length);
      }
    } catch (err) {
      console.error('Failed to fetch notifications:', err);
    } finally {
      setLoading(false);
    }
  };

  // Register user room when currentUser becomes available
  useEffect(() => {
    if (!currentUser?.email) return;

    // emit register to backend so server can join this socket to user's room
    try {
      socket.emit('register', currentUser.email);
      // optional: console log for debugging
      console.log('🧩 socket register emitted for:', currentUser.email);
    } catch (err) {
      console.warn('Socket register emit failed', err);
    }
  }, [currentUser]);

  // Listen for notification events once (socket is singleton from utils/socket)
  useEffect(() => {
    // Handler receives new notification object from backend
    const handleNew = (newNotif: Notification) => {
      console.log('📩 Received notification:new', newNotif);
      setNotifications((prev) => [newNotif, ...prev]);
      setUnreadCount((prev) => prev + 1);
      showSystemNotification(
        "New Notification",
        newNotif.title || "You have a new notification"
      );
    };

    socket.on('notification:new', handleNew);


    return () => {
      socket.off('notification:new', handleNew);
    };
  }, []);

  // initial load
  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        setNotifications,
        unreadCount,
        setUnreadCount,
        loading,
        fetchNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotification = (): NotificationContextType => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return ctx;
};
